import { Circle, Group, Text } from "react-konva";
import type { BoardToken } from "@/lib/canvas/board-schema";
import { pointToPx, type Dims } from "@/lib/canvas/coords";

/**
 * Player / ball token renderer for the tactical board.
 *
 * - **Player**: filled circle ~3% of the longer axis in radius +
 *   centered label text. The label is typically a jersey number ("9")
 *   or role string ("GK") from `FORMATION_PRESETS`.
 * - **Ball**: smaller white circle with a thin dark border. Tokens
 *   never carry color overrides — they're visually distinct from
 *   shapes by shape (filled vs stroked), not color.
 *
 * Selection state: a thin amber outline ring around the token. Same
 * convention as `ShapeRenderer`'s selection treatment.
 */

const SELECTED_COLOR = "#fbbf24";
const PLAYER_FILL = "#0ea5e9";
const PLAYER_BORDER = "rgba(15, 23, 42, 0.75)";
const PLAYER_LABEL = "#ffffff";
const BALL_FILL = "#ffffff";
const BALL_BORDER = "#0f172a";

export type TokenRendererProps = {
  token: BoardToken;
  dims: Dims;
  selected?: boolean;
  onSelect?: () => void;
  draggable?: boolean;
  onDragEnd?: (token: BoardToken) => void;
};

export function TokenRenderer(props: TokenRendererProps) {
  const { token, dims, selected, onSelect, draggable, onDragEnd } = props;
  const center = pointToPx({ x: token.x, y: token.y }, dims);
  const radius = Math.min(dims.width, dims.height) * 0.028;

  if (token.kind === "ball") {
    return (
      <Group
        id={token.id}
        x={center.x}
        y={center.y}
        onClick={onSelect}
        onTap={onSelect}
        draggable={draggable}
        onDragEnd={(e) => {
          if (!onDragEnd) return;
          const newX = clamp01(e.target.x() / dims.width);
          const newY = clamp01(e.target.y() / dims.height);
          onDragEnd({ ...token, x: newX, y: newY });
        }}
      >
        <Circle
          radius={radius * 0.7}
          fill={BALL_FILL}
          stroke={selected ? SELECTED_COLOR : BALL_BORDER}
          strokeWidth={selected ? 3 : 1.5}
        />
      </Group>
    );
  }

  // Player token
  const label = token.label?.trim() ?? "";
  // Pre-measure: short labels fit in a smaller font, longer ones
  // shrink to stay inside the circle.
  const fontSize = label.length > 2 ? Math.max(11, radius * 0.8) : Math.max(13, radius * 1.1);

  return (
    <Group
      id={token.id}
      x={center.x}
      y={center.y}
      onClick={onSelect}
      onTap={onSelect}
      draggable={draggable}
      onDragEnd={(e) => {
        if (!onDragEnd) return;
        const newX = clamp01(e.target.x() / dims.width);
        const newY = clamp01(e.target.y() / dims.height);
        onDragEnd({ ...token, x: newX, y: newY });
      }}
    >
      <Circle
        radius={radius}
        fill={PLAYER_FILL}
        stroke={selected ? SELECTED_COLOR : PLAYER_BORDER}
        strokeWidth={selected ? 3 : 1.5}
      />
      {label ? (
        <Text
          text={label}
          fontSize={fontSize}
          fontStyle="700"
          fontFamily="ui-sans-serif, system-ui, sans-serif"
          fill={PLAYER_LABEL}
          // Centre the text: offset by half its measured width/height.
          // Konva can't compute measureText synchronously without a
          // ref, so use an approximation that's tight for 1-3 char
          // labels (the common case from FORMATION_PRESETS).
          offsetX={(label.length * fontSize * 0.32)}
          offsetY={fontSize * 0.5}
          listening={false}
        />
      ) : null}
    </Group>
  );
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}
